import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Avatar from "@/components/Avatar";
import { Loader2, ChevronDown, Swords, Trophy } from "lucide-react";

function Picker({ label, players, value, onChange, testid }) {
  return (
    <div className="flex-1">
      <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 block mb-1">{label}</label>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="appearance-none w-full bg-[#141414] border border-white/10 pl-4 pr-10 py-3 rounded-sm text-sm focus:ring-2 focus:ring-[#E31837] focus:outline-none"
          data-testid={testid}
        >
          <option value="">Select a player…</option>
          {players.map((p) => <option key={p.username} value={p.username}>{p.username}</option>)}
        </select>
        <ChevronDown className="w-4 h-4 absolute right-3 top-3.5 pointer-events-none text-white/50" />
      </div>
    </div>
  );
}

function PlayerHead({ data, wins }) {
  if (!data) return <div className="flex-1" />;
  return (
    <div className="flex-1 flex flex-col items-center gap-2 text-center">
      <Avatar team={data.avatar} username={data.username} size="lg" />
      <div className="font-['Bebas_Neue'] text-3xl tracking-wide leading-none">{data.username}</div>
      <div className="text-xs text-white/40">Rank #{data.rank} · {data.season_total} pts</div>
      <div className="font-['Bebas_Neue'] text-5xl tracking-wide text-[#E31837] leading-none">{wins}</div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">Weeks Won H2H</div>
    </div>
  );
}

export default function HeadToHead() {
  const [players, setPlayers] = useState([]);
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  const [da, setDa] = useState(null);
  const [db, setDb] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/players").then((res) => setPlayers(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!a) { setDa(null); return; }
    api.get(`/profile/${encodeURIComponent(a)}`).then((res) => setDa(res.data)).catch(() => setDa(null));
  }, [a]);
  useEffect(() => {
    if (!b) { setDb(null); return; }
    setLoading(true);
    api.get(`/profile/${encodeURIComponent(b)}`).then((res) => setDb(res.data)).catch(() => setDb(null)).finally(() => setLoading(false));
  }, [b]);

  const both = da && db;
  let aWins = 0, bWins = 0;
  if (both) {
    da.weekly.forEach((wa, i) => {
      const wb = db.weekly[i];
      if (wa.decided > 0 && (wa.played || wb.played)) {
        if (wa.total > wb.total) aWins++;
        else if (wb.total > wa.total) bWins++;
      }
    });
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <div className="flex items-center gap-3 mb-2">
        <Swords className="w-7 h-7 text-[#E31837]" />
        <h1 className="font-['Bebas_Neue'] text-5xl tracking-tight leading-none">HEAD TO HEAD</h1>
      </div>
      <p className="text-white/50 text-sm mb-6">Stack any two players' weekly scores side by side.</p>

      <div className="flex flex-col sm:flex-row items-end gap-3 mb-8">
        <Picker label="Player A" players={players.filter((p) => p.username !== b)} value={a} onChange={setA} testid="h2h-player-a" />
        <div className="hidden sm:block font-['Bebas_Neue'] text-3xl text-white/30 pb-2">VS</div>
        <Picker label="Player B" players={players.filter((p) => p.username !== a)} value={b} onChange={setB} testid="h2h-player-b" />
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-white/50 py-16 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>
      ) : !both ? (
        <div className="bg-[#141414] border border-white/10 px-6 py-16 text-center text-white/50" data-testid="h2h-empty">
          Pick two players to compare.
        </div>
      ) : (
        <div data-testid="h2h-result">
          <div className="bg-[#141414] border border-white/10 p-6 flex items-center gap-4 mb-6">
            <PlayerHead data={da} wins={aWins} />
            <div className="flex flex-col items-center shrink-0" data-testid="h2h-record">
              <div className="font-['Bebas_Neue'] text-2xl text-white/30 leading-none">VS</div>
              <div className="font-['Bebas_Neue'] text-4xl tracking-wide leading-none mt-2">
                <span className={aWins > bWins ? "text-[#22C55E]" : "text-white/70"}>{aWins}</span>
                <span className="text-white/30">–</span>
                <span className={bWins > aWins ? "text-[#22C55E]" : "text-white/70"}>{bWins}</span>
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-1">H2H Record</div>
            </div>
            <PlayerHead data={db} wins={bWins} />
          </div>

          <div className="border border-white/10 overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-[#0A0A0A] text-[10px] uppercase tracking-[0.2em] text-white/40">
                  <th className="text-left px-4 py-3 border-b border-white/10">Week</th>
                  <th className="text-center px-4 py-3 border-b border-white/10">{da.username}</th>
                  <th className="text-center px-4 py-3 border-b border-white/10"></th>
                  <th className="text-center px-4 py-3 border-b border-white/10">{db.username}</th>
                </tr>
              </thead>
              <tbody>
                {da.weekly.map((wa, i) => {
                  const wb = db.weekly[i];
                  const played = wa.decided > 0 && (wa.played || wb.played);
                  const aLead = played && wa.total > wb.total;
                  const bLead = played && wb.total > wa.total;
                  return (
                    <tr key={wa.week_number} className="hover:bg-white/5 transition-colors duration-150" data-testid={`h2h-week-${wa.week_number}`}>
                      <td className="px-4 py-2.5 border-b border-white/10 font-medium">Week {wa.week_number}</td>
                      <td className={`text-center px-4 py-2.5 border-b border-white/10 font-['Bebas_Neue'] text-xl ${aLead ? "text-[#22C55E]" : "text-white/70"}`}>
                        {played ? wa.total : "–"}
                      </td>
                      <td className="text-center px-4 py-2.5 border-b border-white/10">
                        {aLead && <Trophy className="w-3.5 h-3.5 text-[#EAB308] inline" />}
                        {bLead && <Trophy className="w-3.5 h-3.5 text-[#EAB308] inline" />}
                        {played && !aLead && !bLead && <span className="text-white/30 text-xs">tie</span>}
                      </td>
                      <td className={`text-center px-4 py-2.5 border-b border-white/10 font-['Bebas_Neue'] text-xl ${bLead ? "text-[#22C55E]" : "text-white/70"}`}>
                        {played ? wb.total : "–"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
